import React from 'react';
import { Button } from './ui/Button';
import { Calculator, TrendingUp, FileText } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
export function Hero() {
  const {
    t
  } = useLang();
  return <div className="relative min-h-[85vh] flex items-center pt-28 pb-20 bg-white overflow-hidden" data-id="element-221">
      {/* Subtle geometric background pattern */}
      <div className="absolute inset-0 opacity-[0.03] hidden lg:block" data-id="element-222">
        <div className="absolute top-20 left-10 w-64 h-64 border border-black rounded-full" data-id="element-223"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 border border-black" data-id="element-224"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-black rotate-45" data-id="element-225"></div>
      </div>

      <div className="container mx-auto px-5 md:px-8 max-w-7xl relative z-10 w-full" data-id="element-226">
        {/* DESKTOP: two-column side by side */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-12 lg:gap-20 items-stretch" data-id="element-227">
          {/* Left: text */}
          <div className="flex flex-col justify-center" data-id="element-228">
            <div className="inline-block mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-229">
              <span className="py-1 border-b border-black text-sm font-medium text-black uppercase tracking-wider" data-id="element-230">
                {t('hero_badge')}
              </span>
            </div>

            <h1 className="text-5xl xl:text-7xl font-bold text-black leading-[0.95] tracking-tight mb-8 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100" data-id="element-231">
              {t('hero_h1_1')} <br data-id="element-232" />
              {t('hero_h1_2')}
            </h1>

            <p className="text-xl text-neutral-500 mb-12 leading-relaxed max-w-xl font-light animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200" data-id="element-233">
              {t('hero_subtitle')}
            </p>

            <div className="flex gap-4 items-start animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300" data-id="element-234">
              <Button variant="primary" size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-235">
                {t('hero_cta_primary')}
              </Button>
              <Button variant="outline" size="lg" onClick={() => document.getElementById('services')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-236">
                {t('hero_cta_secondary')}
              </Button>
            </div>

            <div className="mt-16 pt-8 border-t border-neutral-100 grid grid-cols-3 gap-8 animate-in fade-in duration-700 delay-500" data-id="element-237">
              <div className="flex items-center gap-3" data-id="element-238">
                <Calculator className="h-5 w-5 text-black flex-shrink-0" data-id="element-239" />
                <span className="text-sm text-neutral-600" data-id="element-240">
                  {t('hero_trust_1')}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-241">
                <TrendingUp className="h-5 w-5 text-black flex-shrink-0" data-id="element-242" />
                <span className="text-sm text-neutral-600" data-id="element-243">
                  {t('hero_trust_2')}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-244">
                <FileText className="h-5 w-5 text-black flex-shrink-0" data-id="element-245" />
                <span className="text-sm text-neutral-600" data-id="element-246">
                  {t('hero_trust_3')}
                </span>
              </div>
            </div>
          </div>

          {/* Right: image */}
          <div className="animate-in fade-in slide-in-from-bottom-6 duration-700 delay-200" data-id="element-247">
            <div className="relative h-full min-h-[520px]" data-id="element-248">
              <div className="w-full h-full border border-neutral-200 overflow-hidden bg-neutral-50" data-id="element-249">
                <img src="https://cdn.magicpatterns.com/uploads/cAfNZfbseXB1vTZS8uLWUn/IMG_4240.jpg" alt="Бухгалтерські послуги" className="w-full h-full object-cover object-center" data-id="element-250" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-neutral-200 p-4 shadow-sm z-10" data-id="element-251">
                <div className="text-2xl font-bold text-black" data-id="element-252">100%</div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider mt-0.5" data-id="element-253">
                  {t('hero_stat_label')}
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-16 h-16 border border-neutral-200 -z-10" data-id="element-254"></div>
            </div>
          </div>
        </div>

        {/* MOBILE: stacked — badge → h1 → image → subtitle → buttons → trust */}
        <div className="lg:hidden flex flex-col" data-id="element-255">
          {/* Badge */}
          <div className="mb-5 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-256">
            <span className="py-1 border-b border-black text-xs font-medium text-black uppercase tracking-wider" data-id="element-257">
              {t('hero_badge')}
            </span>
          </div>

          {/* Heading */}
          <h1 className="text-4xl sm:text-5xl font-bold text-black leading-[0.95] tracking-tight mb-6 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100" data-id="element-258">
            {t('hero_h1_1')} <br data-id="element-259" />
            {t('hero_h1_2')}
          </h1>

          {/* Hero image — visible on mobile between heading and text */}
          <div className="relative mb-6 animate-in fade-in duration-700 delay-150" data-id="element-260">
            <div className="w-full h-64 sm:h-80 border border-neutral-200 overflow-hidden bg-neutral-50" data-id="element-261">
              <img src="https://cdn.magicpatterns.com/uploads/cAfNZfbseXB1vTZS8uLWUn/IMG_4240.jpg" alt="Бухгалтерські послуги" className="w-full h-full object-cover object-center" data-id="element-262" />
            </div>
            {/* Stat badge */}
            <div className="absolute bottom-3 left-3 bg-white border border-neutral-200 px-3 py-2 shadow-sm" data-id="element-263">
              <div className="text-lg font-bold text-black leading-none" data-id="element-264">
                100%
              </div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5" data-id="element-265">
                {t('hero_stat_label')}
              </div>
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-base sm:text-lg text-neutral-500 mb-7 leading-relaxed font-light animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200" data-id="element-266">
            {t('hero_subtitle')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col gap-3 mb-8 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300" data-id="element-267">
            <Button variant="primary" size="lg" className="w-full" onClick={() => document.getElementById('contact')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-268">
              {t('hero_cta_primary')}
            </Button>
            <Button variant="outline" size="lg" className="w-full" onClick={() => document.getElementById('services')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-269">
              {t('hero_cta_secondary')}
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="pt-6 border-t border-neutral-100 flex flex-col gap-3 animate-in fade-in duration-700 delay-500" data-id="element-270">
            <div className="flex items-center gap-3" data-id="element-271">
              <Calculator className="h-4 w-4 text-black flex-shrink-0" data-id="element-272" />
              <span className="text-sm text-neutral-600" data-id="element-273">
                {t('hero_trust_1')}
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-274">
              <TrendingUp className="h-4 w-4 text-black flex-shrink-0" data-id="element-275" />
              <span className="text-sm text-neutral-600" data-id="element-276">
                {t('hero_trust_2')}
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-277">
              <FileText className="h-4 w-4 text-black flex-shrink-0" data-id="element-278" />
              <span className="text-sm text-neutral-600" data-id="element-279">
                {t('hero_trust_3')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}